<template>
  <div
    ref="root"
    class="wc"
    role="region"
    aria-label="Workflow canvas"
    data-test="wf-canvas"
    @dragover="onOver"
    @dragleave="onLeave"
    @drop="onDrop"
  >
    <div v-if="scene.blocks.length || scene.slot" class="wc__stage" :style="{ width: scene.contentWidth + 'px', height: scene.contentHeight + 'px' }">
      <svg class="wc__edges" :width="scene.contentWidth" :height="scene.contentHeight" aria-hidden="true" focusable="false">
        <path
          v-for="e in scene.edges"
          :key="e.key"
          :d="e.d"
          fill="none"
          :stroke="e.dashed ? '#93cfbc' : '#b8c2bf'"
          stroke-width="1.5"
          :stroke-dasharray="e.dashed ? '5 5' : null"
        />
      </svg>

      <div
        v-for="b in scene.blocks"
        :key="b.id"
        class="wc-block"
        :class="{
          'wc-block--trigger': b.kind === 'trigger',
          'is-selected': b.selected,
          'is-error': b.preview && b.preview.tone === 'error',
          'is-clickable': b.clickable,
          'is-asking': removingId === b.id,
        }"
        :style="{ left: b.x + 'px', top: b.y + 'px', width: b.w + 'px', height: b.h + 'px' }"
        data-test="wf-block"
        :data-type="b.type"
        :data-kind="b.kind"
        :data-id="b.id"
        @click="onBlock(b)"
      >
        <div class="wc-block__head">
          <span class="wc-block__tile" :class="{ 'wc-block__tile--solid': b.kind === 'trigger' }"><WfIcon :name="b.icon" :size="17" /></span>
          <div class="wc-block__text">
            <div class="wc-block__eyebrow" :class="{ 'wc-block__eyebrow--teal': b.kind === 'trigger' }">{{ b.kind === 'trigger' ? 'When' : 'Then' }}</div>
            <div class="wc-block__label">{{ b.label }}</div>
          </div>
          <button
            v-if="b.hasMenu"
            type="button"
            class="wc-block__menu"
            title="Settings"
            aria-label="Open settings"
            data-test="wf-block-menu"
            @click.stop="$emit('open', b.id)"
          ><WfIcon name="dots" :size="16" :stroke="2.4" /></button>
        </div>

        <div v-if="b.preview && b.preview.text" class="wc-block__preview" :class="`wc-block__preview--${b.preview.tone}`" data-test="wf-block-preview">
          <span class="wc-block__dot"></span>
          <span class="wc-block__preview-text">{{ b.preview.text }}</span>
        </div>

        <span v-if="b.kind !== 'trigger'" class="wc-block__point wc-block__point--top"></span>
        <span class="wc-block__point wc-block__point--bottom"></span>

        <button
          type="button"
          class="wc-block__remove"
          title="Remove this block"
          aria-label="Remove this block"
          data-test="wf-block-remove"
          @click.stop="$emit('ask-remove', b.id)"
        ><WfIcon name="trash" :size="13" :stroke="2" /></button>

        <div
          v-if="removingId === b.id"
          class="wc-ask"
          role="alertdialog"
          aria-label="Remove this block?"
          data-test="wf-block-remove-ask"
          @click.stop
          @keydown.esc.stop="$emit('cancel-remove')"
        >
          <div class="wc-ask__title">Remove this block?</div>
          <div v-if="b.removeNote" class="wc-ask__text">{{ b.removeNote }}</div>
          <div class="wc-ask__actions">
            <button type="button" class="wc-ask__btn wc-ask__btn--ghost" data-test="wf-block-remove-cancel" @click="$emit('cancel-remove')">Cancel</button>
            <button type="button" class="wc-ask__btn wc-ask__btn--danger" data-test="wf-block-remove-confirm" @click="$emit('confirm-remove', b.id)">Remove</button>
          </div>
        </div>
      </div>

      <!-- The empty box at the end of the row (or under the WHEN block / in its place). -->
      <div
        v-if="scene.slot"
        class="wc-slot"
        :class="{ 'is-hot': slotHot }"
        :style="{ left: scene.slot.x + 'px', top: scene.slot.y + 'px', width: scene.slot.w + 'px', height: scene.slot.h + 'px' }"
        data-test="wf-slot"
        :data-kind="scene.slot.kind"
      >
        <WfIcon name="plus" :size="16" :stroke="2.2" />{{ slotText }}
      </div>
    </div>

    <!-- Nothing on the canvas yet: the big drop target. -->
    <div v-if="scene.mode === 'empty'" class="wc-drop" :class="{ 'is-hot': hotTrigger }" data-test="wf-dropzone">
      <span class="wc-drop__tile"><WfIcon name="zap" :size="22" :stroke="1.8" /></span>
      <div class="wc-drop__title">{{ hotTrigger ? 'Drop to start the workflow' : 'Drag a trigger here' }}</div>
      <div class="wc-drop__text">A trigger is the event in Open Dental that starts this workflow. Pick one from the list on the left.</div>
    </div>

    <!-- Every trigger already runs a workflow. -->
    <div v-if="scene.mode === 'none-left'" class="wc-none" data-test="wf-none-left">
      <span class="wc-none__tile"><WfIcon name="workflow" :size="24" :stroke="1.8" /></span>
      <div class="wc-none__title">Nothing new to start here</div>
      <div class="wc-none__text">Every trigger you can use is already running a workflow. Open one from the list on the left to add actions to it.</div>
      <button type="button" class="wc-none__btn" data-test="wf-back-to-workflows" @click="$emit('back')">
        <WfIcon name="arrowL" :size="16" :stroke="2" />
        Back to workflows
      </button>
    </div>

    <!-- The workflow this page was opened on could not be read. -->
    <div v-if="scene.mode === 'load-failed'" class="wc-none" role="alert" data-test="wf-load-failed">
      <span class="wc-none__tile wc-none__tile--error"><WfIcon name="alert" :size="24" :stroke="1.8" /></span>
      <div class="wc-none__title">Couldn’t open this workflow</div>
      <div class="wc-none__text">It may have been deleted, or the connection dropped. Nothing was changed. Try again, or go back to your workflows.</div>
      <div class="wc-none__buttons">
        <button type="button" class="wc-none__btn wc-none__btn--primary" data-test="wf-load-retry" @click="$emit('retry')">Try again</button>
        <button type="button" class="wc-none__btn" data-test="wf-load-back" @click="$emit('back')">
          <WfIcon name="arrowL" :size="16" :stroke="2" />
          Back to workflows
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The builder's canvas (design frames 1c-1f, 1r, 1u; Workflow overhaul P3, 2026-10-10). Presentational: the
 * page turns its nodes into a `scene` (src/utils/workflow/canvasGraph.js `layout`) and this draws it —
 * the dot grid, the WHEN block, the THEN row, the curved links, the dashed slot, the empty and
 * "nothing new" states — and hands back what the user did (open a block, ask to remove one, drop a block from
 * the palette). Blocks are never dragged: where they sit is decided by the layout, not by the hand.
 *
 * The canvas measures itself (a ResizeObserver) and reports its width, so the page can centre the row and
 * decide when it must scroll. When the row is wider than the canvas the canvas scrolls sideways (and only it).
 *
 * scene = {
 *   mode: 'graph' | 'empty' | 'none-left' | 'load-failed',   (load-failed: emits `retry`, or `back`)
 *   contentWidth, contentHeight,
 *   blocks: [{ id, kind: 'trigger' | 'action', type, label, icon, x, y, w, h, hasMenu, clickable, selected,
 *              preview: { text, tone: 'ok' | 'warn' | 'error' } | null, removeNote }],
 *   edges:  [{ key, d, dashed }],
 *   slot:   { kind: 'action' | 'trigger', x, y, w, h } | null,
 * }
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID (mixin-prop-shadow-guardrail).
 */
import WfIcon from './WfIcon.vue';

export default {
  name: 'BuilderCanvas',

  components: { WfIcon },

  props: {
    scene: { type: Object, required: true },
    /** What is being dragged from the palette: 'trigger' | 'action' | '' (nothing). */
    dragKind: { type: String, default: '' },
    /** The drag is over the canvas. */
    hot: { type: Boolean, default: false },
    /** The id of the block whose "Remove this block?" ask is open, or null. */
    removingId: { type: [Number, String], default: null },
  },

  data() {
    return { observer: null };
  },

  computed: {
    hotTrigger() {
      return this.hot && this.dragKind === 'trigger';
    },

    slotHot() {
      if (!this.scene.slot) return false;
      return this.hot && this.dragKind === this.scene.slot.kind;
    },

    slotText() {
      const kind = this.scene.slot ? this.scene.slot.kind : '';
      if (kind === 'trigger') return this.slotHot ? 'Drop to start the workflow' : 'Drag a trigger here';
      return this.slotHot ? 'Drop to connect' : 'Drag an action here';
    },
  },

  watch: {
    removingId(id) {
      if (id === null || id === undefined) return;
      // The ask is a small dialog: put focus on the safe choice so Escape and Enter act on it.
      this.$nextTick(() => {
        const btn = this.$el && this.$el.querySelector('[data-test="wf-block-remove-cancel"]');
        if (btn && typeof btn.focus === 'function') btn.focus();
      });
    },
  },

  mounted() {
    this.measure();
    if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function') {
      this.observer = new window.ResizeObserver(() => this.measure());
      this.observer.observe(this.$el);
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },

  methods: {
    measure() {
      const el = this.$el;
      if (!el) return;
      this.$emit('measure', Math.max(0, Math.floor(el.clientWidth || 0)));
    },

    onBlock(b) {
      if (b.clickable) this.$emit('open', b.id);
    },

    // A foreign drag (a file, some text) is not ours: only a palette drag is accepted.
    onOver(event) {
      if (!this.dragKind) return;
      event.preventDefault();
      if (!this.hot) this.$emit('hot', true);
    },

    onLeave(event) {
      const to = event.relatedTarget;
      if (to && event.currentTarget && event.currentTarget.contains(to)) return;
      if (this.hot) this.$emit('hot', false);
    },

    onDrop(event) {
      if (!this.dragKind) return;
      event.preventDefault();
      let type = '';
      try {
        type = event.dataTransfer.getData('text/plain') || '';
      } catch (e) {
        type = '';
      }
      this.$emit('hot', false);
      this.$emit('drop', type);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './workflow-tokens';

.wc {
  position: relative;
  flex: 1;
  min-width: 0;
  overflow: auto;
  background-color: $fu-neutral-25;
  background-image: radial-gradient(circle, #d3dad8 1px, transparent 1.3px);
  background-size: 22px 22px;
  background-attachment: local;
}

.wc__stage {
  position: relative;
}

.wc__edges {
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  pointer-events: none;
}

// ---- A block -----------------------------------------------------------------------------------
.wc-block {
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border: 1px solid $fu-border;
  border-radius: 14px;
  background: $fu-surface;
  box-shadow: $fu-shadow-sm;

  &--trigger { border-color: $fu-brand-200; }
  &.is-clickable { cursor: pointer; }
  &.is-selected { border: 1.5px solid $fu-brand-400; box-shadow: $fu-ring; }
  &.is-error { border: 1.5px solid #f0b4b6; }
}

.wc-block__head {
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;
  padding: 12px 8px 12px 12px;
}

.wc-block__tile {
  @include wb-tile(34px, 9px);

  &--solid {
    background: $fu-brand;
    color: #ffffff;
  }
}

.wc-block__text {
  flex: 1;
  min-width: 0;
}

.wc-block__eyebrow {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $fu-neutral-500;

  &--teal { color: $fu-brand-700; }
}

.wc-block__label {
  font-size: 14.5px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.wc-block__menu {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  flex: none;
  padding: 6px;
  border-radius: 8px;
  color: $fu-neutral-400;

  &:hover { background: $fu-neutral-100; color: $fu-neutral-700; }
}

.wc-block__preview {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: auto;
  padding: 9px 12px 11px;
  border-top: 1px solid $fu-neutral-100;
  font-size: 12.5px;
  line-height: 1.35;
  color: $fu-neutral-600;

  &--error { color: $fu-danger-text; }
}

.wc-block__dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $fu-brand-500;

  .wc-block__preview--warn & { background: $fu-warning; }
  .wc-block__preview--error & { background: $fu-danger; }
}

.wc-block__preview-text {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-block__point {
  position: absolute;
  left: 50%;
  width: 10px;
  height: 10px;
  margin-left: -5px;
  border: 1.5px solid $fu-neutral-300;
  border-radius: 50%;
  background: $fu-surface;

  &--top { top: -5px; }
  &--bottom { bottom: -5px; }
}

// Remove: a small round button on the block's corner, shown on hover / focus (always, on a touch screen).
.wc-block__remove {
  @include fu-button-reset;
  @include fu-focus-ring;
  position: absolute;
  top: -10px;
  right: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid $fu-border;
  border-radius: 50%;
  background: $fu-surface;
  color: $fu-neutral-500;
  box-shadow: $fu-shadow-xs;
  opacity: 0;
  transition: opacity 0.12s ease;

  &:hover { background: $fu-danger-subtle; border-color: $fu-danger-border; color: $fu-danger-text; }

  .wc-block:hover &,
  .wc-block:focus-within &,
  .wc-block.is-asking & { opacity: 1; }

  @media (hover: none) { opacity: 1; }
}

.wc-ask {
  @include wb-popover(240px);
  top: calc(100% + 8px);
  right: 0;
  cursor: default;
}

.wc-ask__title {
  font-size: 14px;
  font-weight: 700;
}

.wc-ask__text {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-600;
}

.wc-ask__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.wc-ask__btn {
  &--ghost { @include fu-btn(ghost, sm); }
  &--danger { @include fu-btn(danger, sm); }
}

// ---- The slot ----------------------------------------------------------------------------------
.wc-slot {
  @include wb-dashed(false);
  position: absolute;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.8);
  border-color: $fu-brand-300;
  font-size: 13.5px;
  font-weight: 700;
  color: $fu-brand-700;
  text-align: center;

  &.is-hot {
    @include wb-dashed(true);
    color: $fu-brand-800;
  }
}

// ---- Empty canvas ------------------------------------------------------------------------------
.wc-drop {
  @include wb-dashed(false);
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 340px;
  max-width: calc(100% - 32px);
  padding: 28px 24px;
  border-radius: 18px;
  text-align: center;

  &.is-hot { @include wb-dashed(true); }
}

.wc-drop__tile {
  @include wb-tile(46px, 13px);
}

.wc-drop__title {
  margin-top: 4px;
  font-size: 16px;
  font-weight: 800;
}

.wc-drop__text {
  font-size: 13.5px;
  line-height: 1.5;
  color: $fu-neutral-500;
}

.wc-none {
  position: absolute;
  top: 42%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 380px;
  max-width: calc(100% - 32px);
  text-align: center;
}

.wc-none__tile {
  @include wb-tile(46px, 13px);
  background: $fu-neutral-100;
  color: $fu-neutral-500;
}

.wc-none__title {
  margin-top: 4px;
  font-size: 17px;
  font-weight: 800;
}

.wc-none__text {
  font-size: 13.5px;
  line-height: 1.5;
  color: $fu-neutral-500;
}

.wc-none__tile--error {
  background: $fu-danger-subtle;
  color: $fu-danger;
}

.wc-none__buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
}

.wc-none__btn {
  @include fu-btn(secondary, md);
  margin-top: 8px;
  height: 40px;

  .wc-none__buttons & { margin-top: 0; }

  &--primary { @include fu-btn(primary, md); height: 40px; }
}
</style>
